/**
 * Use Cases section component for Stack Forward website
 */

import React from 'react';

/**
 * Use case interface
 */
interface UseCase {
  title: string;
  description: string;
  icon: string;
  industries: string[];
  solutions: string[];
}

/**
 * Use Cases section component
 */
export const UseCases: React.FC = () => {
  // Use cases data
  const useCases: UseCase[] = [
    {
      title: 'Project-Based SMEs',
      description: 'Small to medium enterprises managing multiple projects need integrated systems for resource planning, time tracking, and client management.',
      icon: '🏗️',
      industries: ['Consulting', 'Agencies', 'Professional Services', 'Freelancers'],
      solutions: ['ERPNext Projects', 'Odoo CRM', 'Mattermost Teams', 'Nextcloud Files'],
    },
    {
      title: 'Manufacturing & Inventory',
      description: 'Manufacturing companies require robust inventory management, production planning, and supply chain coordination.',
      icon: '🏭',
      industries: ['Manufacturing', 'Distribution', 'Retail', 'E-commerce'],
      solutions: ['ERPNext Manufacturing', 'Odoo Inventory', 'MinIO Storage', 'GitLab CI/CD'],
    },
    {
      title: 'Field Service Teams',
      description: 'Organizations with mobile teams need real-time communication, file sharing, and project coordination capabilities.',
      icon: '🚚',
      industries: ['Field Services', 'Maintenance', 'Installation', 'Support'],
      solutions: ['Mattermost Mobile', 'Nextcloud Sync', 'ERPNext Projects', 'Keycloak SSO'],
    },
    {
      title: 'Regulated Industries (On-Prem)',
      description: 'Industries with strict compliance requirements need secure, self-hosted solutions with full data control.',
      icon: '🏛️',
      industries: ['Healthcare', 'Finance', 'Government', 'Legal'],
      solutions: ['On-Premise Hosting', 'Keycloak Security', 'Nextcloud Compliance', 'Audit Logging'],
    },
    {
      title: 'Cost-Optimization from SaaS to OSS',
      description: 'Companies looking to reduce licensing costs while maintaining functionality migrate from proprietary SaaS to open-source alternatives.',
      icon: '💰',
      industries: ['Any Industry', 'Growing Companies', 'Cost-Conscious Teams'],
      solutions: ['Migration Planning', 'Data Transfer', 'Training', 'Ongoing Support'],
    },
  ];

  return (
    <section id="use-cases" className="section bg-white">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-4">
            Use Cases
          </h2>
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            Discover how different industries leverage open-source solutions to 
            streamline operations, reduce costs, and maintain control over their technology stack.
          </p>
        </div>

        {/* Use cases grid */}
        <div className="space-y-12">
          {useCases.map((useCase, index) => (
            <div
              key={useCase.title}
              className={`flex flex-col lg:flex-row gap-8 items-center ${
                index % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Content */}
              <div className="flex-1 space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 flex items-center justify-center text-4xl bg-primary-100 rounded-xl">
                    {useCase.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-neutral-900">
                    {useCase.title}
                  </h3>
                </div>

                <p className="text-lg text-neutral-600 leading-relaxed">
                  {useCase.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Industries */}
                  <div>
                    <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-3">
                      Industries
                    </h4>
                    <ul className="space-y-2">
                      {useCase.industries.map((industry, industryIndex) => (
                        <li
                          key={industryIndex}
                          className="flex items-center text-neutral-600"
                        >
                          <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3 flex-shrink-0"></span>
                          {industry}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Solutions */}
                  <div>
                    <h4 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide mb-3">
                      Our Solutions
                    </h4>
                    <ul className="space-y-2">
                      {useCase.solutions.map((solution, solutionIndex) => (
                        <li
                          key={solutionIndex}
                          className="flex items-center text-neutral-600"
                        >
                          <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3 flex-shrink-0"></span>
                          {solution}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <button
                  onClick={() => {
                    const contactSection = document.querySelector('#contact');
                    if (contactSection) {
                      contactSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className="btn-secondary"
                >
                  Discuss Your Use Case
                </button>
              </div>

              {/* Visual placeholder */}
              <div className="flex-1 max-w-md">
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8 text-center">
                  <div className="text-6xl mb-4">{useCase.icon}</div>
                  <h4 className="text-lg font-semibold text-primary-800 mb-2">
                    {useCase.title}
                  </h4>
                  <p className="text-primary-600 text-sm">
                    Tailored open-source solutions for your industry needs
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Ready to Transform Your Technology Stack?
            </h3>
            <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
              Join hundreds of companies that have successfully migrated to open-source 
              solutions and regained control over their technology infrastructure.
            </p>
            <button
              onClick={() => {
                const contactSection = document.querySelector('#contact');
                if (contactSection) {
                  contactSection.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
            >
              Start Your Journey
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
